import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, FormControlLabel, Radio, RadioGroup, InputAdornment, useMediaQuery } from '@mui/material';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import theme from '../../util/theme/theme';

interface AddTaxPopupProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: { name: string, rate: string, enabled: boolean, type: 'added' | 'included' }) => void;
    isEdit?: { id: number | null, state: boolean };
    taken?: string[];
}

const AddTaxPopup: React.FC<AddTaxPopupProps> = ({ open, onClose, onConfirm, isEdit, taken }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [name, setName] = useState('');
    const [amount, setAmount] = useState('');
    const [type, setType] = useState<'added' | 'included' | ''>('');
    const [originalName, setOriginalName] = useState(''); // Track original name for edit mode
    const [errors, setErrors] = useState<{ name?: string; amount?: string, type?: string }>({});

    const [fetchTaxDetails, setFetchTaxDetails] = useApi();

    React.useEffect(() => {
		if(isEdit?.state && isEdit?.id) {
			const { url, method } = buildApiRequestConfig(routes.taxDefinitions.fetchById, [{ param: 'id', value: isEdit.id }]);
			setFetchTaxDetails({ url, method });
		} else {
			setErrors({});
			setName('');
			setAmount('');
            setOriginalName('');
		}
	}, [isEdit]);

	React.useEffect(() => {
		const { data, isLoading, error } = fetchTaxDetails;
		if (data && !isLoading) {
			setName(data.name);
			setAmount(data.rate?.toFixed(2));
            setType(data.type);
            setOriginalName(data.name); // Store the original name
		}
	}, [fetchTaxDetails]);

    const validate = () => {
        const newError = { name: '', amount: '', type: '' };
        if (!name) {
            newError.name = 'Name is required';
        } else {
            const namesToCheck = taken || [];
            const isDuplicateName = namesToCheck.some(t => t.toLowerCase() === name.toLowerCase());
            
            if (isDuplicateName) {
                // If editing and the name hasn't changed from original, it's not a duplicate
                if (isEdit?.state && originalName.toLowerCase() === name.toLowerCase()) {
                    // Name unchanged, no error
                } else {
                    // Either adding new tax or editing with changed name that conflicts
                    newError.name = 'A tax with this name already exists.';
                }
            }
            
            if (name.length > 50) newError.name = 'Name cannot exceed 50 characters';
        }
        if (!amount) {
            newError.amount = 'Amount is required';
        } else {
            if (isNaN(Number(amount)) || Number(amount) <= 0) newError.amount = 'Amount must be a positive number';
            if (!/^\d+(\.\d{1,2})?$/.test(amount)) newError.amount = 'Amount can have at most 2 decimal places';
        }
        if (!type) newError.type = 'Please select an option';

        setErrors(newError);
        return !!(newError.name || newError.amount || newError.type);
    };

    const handleConfirm = () => {
        const hasErrors = validate();
        if (!hasErrors) {
            onConfirm({ name, rate: amount, enabled: true, type: type as 'added' | 'included' });
            setName('');
            setAmount('');
            setType('');
        }
    };

    const handleClose = () => {
        setName('');
        setAmount('');
        setErrors({});
        setType('');
        setOriginalName('');
        onClose();
    };

    const content = (
        <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>{isEdit?.state ? 'Edit Tax' : 'Add New Tax'}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Box mb={2}>
                <Typography fontSize={14} color='var(--color-text-muted)'>
                    Name <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter Name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    inputProps={{ maxLength: 50 }}
                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                />
                </Box>
                <Box mb={2}>
                <Typography fontSize={14} color='var(--color-text-muted)'>
                    Amount <span style={{ color: '#F44336' }}>*</span>
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter Amount"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    error={!!errors.amount}
                    type='number'
                    helperText={errors.amount}
                    InputProps={{
                        endAdornment: <InputAdornment position="end"><span style={{ marginRight: 8 }}>%</span></InputAdornment>
                    }}
                    sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                />
                </Box>
                <Box sx={{ border: errors.type ? '1px solid #d32f2f' : 'none', borderRadius: 1, boxSizing: 'border-box' }}>
                    <RadioGroup
                        row
                        value={type}
                        onChange={e => setType(e.target.value as 'added' | 'included')}
                        defaultValue="added"
                        name="tax-type"
                        sx={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            boxSizing: 'border-box',
                        }}
                    >
                        <FormControlLabel
                            value="included"
                            control={<Radio sx={{
                                color: '#C4C4C4',
                                height: 20,
                                '&.Mui-checked': { color: 'var(--color-primary)' },
                            }} />}
                            label={
                                <Typography fontSize={14} color="var(--color-text-muted)">
                                    Included In Total
                                </Typography>
                            }
                            disabled={isEdit?.state}
                        />
                        <FormControlLabel
                            value="added"
                            control={<Radio sx={{
                                color: '#C4C4C4',
                                height: 20,
                                '&.Mui-checked': { color: 'var(--color-primary)' }
                            }} />}
                            label={
                                <Typography fontSize={14} color="var(--color-text-muted)">
                                    Added Onto Total
                                </Typography>
                            }
                            disabled={isEdit?.state}
                        />
                    </RadioGroup>
                </Box>
                {errors.type && <Typography fontSize={12} color="#d32f2f" sx={{ mb: 1, ml: 1.75 }}>{errors.type}</Typography>}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button onClick={handleClose} variant="contained" sx={{ textTransform: 'none', background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    variant="contained" 
                    sx={{ 
                        background: 'var(--color-primary)', 
                        color: '#fff', 
                        boxShadow: 'none', 
                        borderRadius: 2, 
                        fontWeight: 600, 
                        width: 160, 
                        '&:hover': { background: 'var(--color-primary)' } 
                }}>
                    {isEdit?.state ? 'Save Changes' : 'Add Tax'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={handleClose} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default AddTaxPopup;
